// src/components/admin/UserImpersonationSelect.jsx
import { useEffect, useState } from 'react';
import { query, where, onSnapshot } from 'firebase/firestore';
import { eventCollection } from '../../event/paths';
import { db } from 'lib/firebase';
import { useAuth } from 'lib/auth';

// MUI
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

export default function UserImpersonationSelect() {
  const {
    realProfile,
    impersonatedProfile,
    setImpersonatedProfile
  } = useAuth();

  const [users, setUsers] = useState([]);

  // 🔒 Admin check is based on the REAL profile, not the impersonated one
  const realRole = realProfile?.type;
  const isAdmin = realRole === 'Admin';

  useEffect(() => {
    if (!isAdmin) return;

    const unsub = onSnapshot(query(eventCollection(db, 'users'), where('status', '==', 'Active')), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(list);
    });

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [isAdmin]);

  // If you are not an admin at all, never show the control
  if (!isAdmin) return null;

  const handleChange = (event) => {
    const selectedId = event.target.value;

    if (!selectedId) {
      // 🧹 stop impersonating and go back to your own profile
      setImpersonatedProfile(null);
      return;
    }

    const selected = users.find((u) => u.id === selectedId);
    setImpersonatedProfile(selected || null);
  };

  const labelForUser = (u) =>
    u?.displayName ||
    u?.name ||
    u?.fullName ||
    u?.email ||
    u?.phone ||
    u?.id;

  // ✅ Only use impersonatedProfile.id if it exists in the users list
  const safeImpersonatedId =
    impersonatedProfile?.id &&
    users.some((u) => u.id === impersonatedProfile.id)
      ? impersonatedProfile.id
      : '';

  return (
    <Box sx={{ ml: 1 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ maxWidth: 520 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="impersonate-user-label">View as user</InputLabel>
          <Select
            labelId="impersonate-user-label"
            label="View as user"
            value={safeImpersonatedId}
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>Stop impersonating</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {labelForUser(u)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {impersonatedProfile ? (
          <Chip
            variant="outlined"
            // 👇 show just the name, keep it on one neat line
            label={labelForUser(impersonatedProfile)}
            sx={{
              maxWidth: 220,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />
        ) : (
          realProfile && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                maxWidth: 220,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Viewing as yourself: {labelForUser(realProfile)}
            </Typography>
          )
        )}
      </Stack>
    </Box>
  );
}
